import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  Group,
  Center,
  Select,
  Button,
  Checkbox,
  Pagination,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconFiles,
} from "@tabler/icons-react";
import DeleteModal from "./DeleteKpiModal";
import AddKPIModal from "./AddKpiModal";
import EditKpiModal from "./EditKpiModal";
import classes from "../KpiPage/kpiPage.module.css";
import { RowDataKPIObjective } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { kpiObjective } from "../types/RowDataTypes";
import { CustomTable } from "../Table/CustomTable";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import Header from "../static/Header";
import getKPIObjectiveTable from "../../DataDisplay/DataDisplayTable/KPIObjectiveTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  OBJECTIVE_ID = "KPI Objective ID",
  TITLE = "Title",
  DESCRIPTION = "Description",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.OBJECTIVE_ID]: "kpi_objective_id",
  [colFilter.TITLE]: "title",
  [colFilter.DESCRIPTION]: "description",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilter.TITLE.toLowerCase(),
    colFilter.DESCRIPTION.toLowerCase(),
  ];
  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <Group justify="space-between">
          <Text className={classes.Text}>{children}</Text>
          <Center className={classes.icon}>
            <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
          </Center>
        </Group>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataKPIObjective[] = [
  {
    objectiveId: "",
    title: "",
    description: "",
    action: "",
  },
];

export function KpiPage() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<keyof RowDataKPIObjective | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const itemsOnPage = useRef(0);

  //Used in searching and when deleting to cause rerender to fetch data
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  //For Searching
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.OBJECTIVE_ID],
    colFilters[colFilter.TITLE],
    colFilters[colFilter.DESCRIPTION],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const [rowDataKPIs, setRowDataKPIs] = useState<RowDataKPIObjective[] | null>(
    null
  );

  // const fetchedData = useFetchRowDataKPI(itemsPerPage, currentPage)
  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    kpiObjective,
    getKPIObjectiveTable().create()
  );

  const [totalRows, setTotalRows] = useState<number>(0);
  let rowData: RowDataKPIObjective[] | null =
    fetchedData.data as RowDataKPIObjective[];
  const isLoading: boolean = fetchedData.isLoading;

  useEffect(() => {
    setRowDataKPIs(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const [isAddKPIModalOpen, setAddKPIModalOpen] = useState(false);
  const [isEditKPIModalOpen, setEditKPIModalOpen] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] =
    useState<RowDataKPIObjective | null>(null);

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataKPIObjective | null>(null);

  const handleAddKPI = () => {
    setAddKPIModalOpen(true);
  };

  const handleAddKPIModalClose = () => {
    setAddKPIModalOpen(false);
  };

  const handleAddKPISuccess = (newKPIData: RowDataKPIObjective) => {
    axiosPrivate
      .post("/api/admin/kpi/objective/create", {
        title: newKPIData.title,
        description: newKPIData.description,
      })
      .then((response) => {
        const data = response.data;
        const returnedData: RowDataKPIObjective = {
          ...newKPIData,
          objectiveId: response.data.objectiveId,
        };

        if (itemsOnPage.current < itemsPerPage) {
          if (!reverseSortDirection)
            setRowDataKPIs((prevData) => [returnedData, ...prevData!!]);
          else setRowDataKPIs((prevData) => [...prevData!!, returnedData]);
        }

        itemsOnPage.current = itemsOnPage.current + 1;
        setTotalRows((prevState) => prevState + 1);
        showSuccessAlert("KPI Objective Successfully Added");
      })
      .catch((error) => {
        // console.log("error " + error);
        showErrorAlert("Something Went Wrong");
      });
  };

  const handleEdit = (row: RowDataKPIObjective) => {
    setSelectedRowForEdit(row);
    setEditKPIModalOpen(true);
  };

  const handleEditKPISuccess = (
    editedKPIObjectiveData: RowDataKPIObjective
  ) => {
    axiosPrivate
      .post(
        "/api/admin/kpi/objective/update",
        {
          id: editedKPIObjectiveData.objectiveId,
          title: editedKPIObjectiveData.title,
          description: editedKPIObjectiveData.description,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then((response) => {
        // console.log("edited data");
        // console.log(response);
        // Find the index of the selectedRow in the sortedData
        const indexOfSelectedRow = rowDataKPIs!!.findIndex(
          (row) => row === selectedRowForEdit
        );

        // Create a copy of the sortedData
        const updatedData = [...rowDataKPIs!!];
        const data = response.data.links;
        // Update the selectedRow in the copied data
        updatedData[indexOfSelectedRow] = {
          ...selectedRowForEdit!!,
          title: editedKPIObjectiveData.title,
          description: editedKPIObjectiveData.description,
        };
        // changeTableState();
        setRowDataKPIs(updatedData);
        showSuccessAlert("Feed Successfully Edited");
      })
      .catch((error) => {
        // console.log("error " + error);
        showErrorAlert("Something Went Wrong");
      });
    setEditKPIModalOpen(false);
  };

  const handleDelete = (row: RowDataKPIObjective) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/kpi/objective/delete", {
        id: selectedRowToDelete!!.objectiveId,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        if (
          itemsOnPage.current == 1 &&
          currentPage !== 1 &&
          currentPage == Math.ceil(totalRows / itemsPerPage)
        )
          setCurrentPage((prev) => prev - 1);
        else changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const setSorting = (field: keyof RowDataKPIObjective) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  const rowsToMap = rowDataKPIs ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td style={{ width: "20%" }}>{row.objectiveId}</Table.Td>
      <Table.Td style={{ width: "20%" }}>{row.title}</Table.Td>
      <Table.Td style={{ width: "20%" }}>{row.description}</Table.Td>
      <Table.Td className={classes.actionButtons} style={{ width: "100%" }}>
        <button onClick={() => handleEdit(row)} className={classes.editbutton}>
          <IconEdit /> Edit
        </button>

        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.bannerPageContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="KPI Objective" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            <Button className={classes.addButton} onClick={handleAddKPI}>
              <IconFiles className={classes.iconFiles} />
              Create Objective KPI
            </Button>
          </div>
        </div>

        <AddKPIModal
          isOpen={isAddKPIModalOpen}
          onClose={handleAddKPIModalClose}
          onAddKPI={handleAddKPISuccess}
        />

        <EditKpiModal
          isOpen={isEditKPIModalOpen}
          onClose={() => setEditKPIModalOpen(false)}
          onEditKPI={handleEditKPISuccess}
          kpiData={selectedRowForEdit || initialData[0]}
        />

        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />

        <CustomTable
          colHeaders={Object.values(ColumnHeaderToFilterPair)}
          filters={Object.values(colFilters)}
          reverseSortDirection={reverseSortDirection}
          sortBy={sortBy}
          setSorting={setSorting}
          customclasses={classes}
          Th={Th}
          rowData={rowDataKPIs}
          rows={rows}
        />

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default KpiPage;
